import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Medal, GraduationCap, CalendarDays, Flag, ArrowRight, HelpCircle, Target } from 'lucide-react';
import PageHero, { CTABlock } from '../components/PageHero';
import SEO from '../components/SEO';
import { exams } from '../data/exams';

const iconMap = { ClipboardList, Medal, GraduationCap, CalendarDays, Flag };

export default function Exams() {
  return (
    <>
      <SEO
        title="Medical Exams for MBBS in Russia — FMGE, NExT, Final State | Russian Education"
        description="Complete guide to the 5 medical exams during & after MBBS in Russia — FMGE, NExT (India licensing), Admission test, semester exams and Final State Exam."
        path="/exams"
        keywords="FMGE, NExT exam, MBBS Russia exams, Russian medical exams, Final State exam Russia"
      />
      <PageHero eyebrow="Exams & Licensing" title="Medical Exams for" highlight="MBBS in Russia"
        subtitle="Complete guide to the academic assessments during your Russian MBBS and the licensing exams (FMGE / NExT) required to practise medicine in India after graduation."
        breadcrumbs={[{ label: 'Exams' }]} />

      <div className="py-14 md:py-20 bg-white">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-brand font-bold">All Medical Exams</div>
          <h2 className="mt-2 font-serif text-3xl md:text-5xl font-bold text-primary">Pick an exam to see full details</h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-3xl">
            Each exam page includes eligibility, format, subject-wise syllabus, fees, preparation tips and FAQs.
          </p>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="exam-list">
            {exams.map((e) => {
              const Icon = iconMap[e.icon] || ClipboardList;
              const accent = e.variant === 'primary' ? 'bg-primary' : 'bg-brand';
              return (
                <Link
                  key={e.slug}
                  to={`/exams/${e.slug}`}
                  data-testid={`exam-card-${e.slug}`}
                  className="group bg-white rounded-2xl border border-border p-6 hover:border-brand/40 hover:shadow-xl transition-all flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl ${accent} text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-brand font-bold">{e.subtitle}</div>
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl font-bold text-primary group-hover:text-brand transition-colors leading-tight">{e.name}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">{e.tagline}</p>

                  <div className="mt-5 pt-4 border-t border-border grid grid-cols-2 gap-3">
                    {e.quickFacts.slice(0, 2).map((f, i) => (
                      <div key={i}>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{f.label}</div>
                        <div className="text-xs font-bold text-primary mt-0.5">{f.value}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 text-sm text-brand font-bold inline-flex items-center gap-1.5">
                    Read full details <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <CTABlock title="Need FMGE / NExT coaching guidance?" subtitle="We partner with the top FMGE coaching institutes and provide guided prep pathways to our alumni." />
    </>
  );
}
